import React from 'react';
import { PropTypes } from 'prop-types';
import {
  Icons,
  NumberField,
  SecurityHelper,
  SingleDatePicker,
  Stack,
  TYPES,
  Inline,
  Button,
  Box,
} from 'sarsaparilla';
import { connect } from 'react-redux';
import moment from 'moment';
import { get, isEqual, toNumber } from 'lodash';
import { bindActionCreators } from 'redux';

import DetailedAvailabilityPageLayout, { LayoutControlContext } from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityPageLayout';
import DetailedAvailabilityPageMain from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityPageMain';
import DetailedAvailabilityFilter from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityFilter';
import DetailedAvailabilityGridAndActions from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityGridAndActions';
import DetailedAvailabilityInformationRequired from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityInformationRequired';
import DetailedAvailabilityPageSide from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityPageSide';
import { shouldUseSmallerGrid } from 'ui-permit/shared-components/utils/layoutUtils';
import DetailedAvailabilityFilterItem from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityFilterItem';
import DivisionDetailsModal from 'ui-permit/shared-components/division-details/DivisionDetailsModal';

import { issuanceStatuses, readRuleAsMoment } from 'ui-permit/src/utils/utils';
import UnauthorizedErrorModal from 'ui-permit/src/components/UnauthorizedErrorModal';
import {
  PERMIT_PRELIMINARY,
  RES_ID_DELIMITER,
  PERMIT,
  AVAILABILITY_DAYS_TO_DISPLAY,
  AVAILABILITY_DAYS_TO_SKIP,
  AVAILABILITY_DAYS_TO_DISPLAY_MOBILE,
  AVAILABILITY_DAYS_TO_SKIP_MOBILE,
  AVAILABILITY_KEY_MAP_FORMAT,
} from '../constants';
import {
  INYO_GROUPSIZE_MAX_PERMIT_TYPE,
  INYO_MT_WHITNEY_EXIT_ID,
  INYO_NF_PERMIT_ID,
  INYO_NF_PERMIT_TYPES,
  MT_WHITNEY_DAY_USE_DIVISION_ID,
  mustVisitMtWhitney,
  isExitingMtWhitney,
  OVERNIGHT_PERMIT
} from '../utils/inyoUtils';
import * as availabilityActions from '../actions/availability';
import BookingErrorModal from '../components/registration/BookingErrorModal';
import * as permitActions from '../actions/permit';
import AvailabilityEnterInyoContainer from './AvailabilityEnterInyo';
import DivisionsMapContainer from './DivisionsMapContainer';
import DivisionFilters from '../components/DivisionFilters';
import { hasAccessToPermit } from '../utils/availabilityUtils';
import CommercialQuestion from '../components/detailed-availability-page/CommercialQuestion';
import AvailabilityLegend from '../components/AvailabilityLegend';

const propTypes = {
  match: TYPES.MATCH,
  history: TYPES.HISTORY,
  location: TYPES.LOCATION,
  account: PropTypes.shape({}),
  areas: PropTypes.arrayOf(PropTypes.string).isRequired,
  dispatch: PropTypes.func,
  divisionSearchCriteria: PropTypes.object,
  error: PropTypes.object,
  issuance: PropTypes.object,
  permit: PropTypes.object,
  permitDate: PropTypes.string,
  permitError: PropTypes.object,
  permitType: PropTypes.string,
  selectedDivision: PropTypes.object,
  selectedStartDate: PropTypes.string,
  selectedStartDate2: PropTypes.string,
  contactAgency: PropTypes.string,
  aActions: PropTypes.shape({
    clearAvailabilitiesMap: PropTypes.func,
    fetchAvailabilityAllDivisions: PropTypes.func,
  }),
  pActions: PropTypes.shape({
    fetchAggregateSeason: PropTypes.func,
    fetchPermit: PropTypes.func,
    fetchRecarea: PropTypes.func,
    setPermitType: PropTypes.func,
    setPermitDate: PropTypes.func,
    setActiveTab: PropTypes.func,
    clearFetchIssuanceError: PropTypes.func,
    showLoginModal: PropTypes.func,
    setDivisionSearchCriteria: PropTypes.func,
    fetchUnpaidIssuance: PropTypes.func,
    setSelectedDivision: PropTypes.func,
    setSelectedDivision2: PropTypes.func,
    clearOvernightCampsite: PropTypes.func,
    updateIssuance: PropTypes.func,
    addItem: PropTypes.func,
    setQuestionnaireAnswers: PropTypes.func,
    updateIssuanceInProgress: PropTypes.func,
  })
};
const defaultProps = {};

export class AvailabilityPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dateFocused: false,
      permitId: props.match.params.permitId,
      existingIssuance: null,
      selectedGroupSize: 0,
      showFilters: false,
      showCommercialAuthModal: false,
      showBookingErrorModal: false,
      waitingForUserAccount: false,
      selectedDivisionId: '',
      isDivisionDetailsModalOpen: false,
    };

    this.onBook = this.onBook.bind(this);
    this.onFiltersApply = this.onFiltersApply.bind(this);
    this.onFiltersClear = this.onFiltersClear.bind(this);
    this.onFiltersClose = this.onFiltersClose.bind(this);
    this.onPermitDateChange = this.onPermitDateChange.bind(this);
    this.renderBookingErrorModal = this.renderBookingErrorModal.bind(this);
  }

  componentDidMount() {
    const permitId = this.state.permitId;

    this.props.pActions.fetchAggregateSeason(permitId);
    this.props.pActions.fetchPermit(permitId)
      .then((permit) => {
        const recAreaId = get(permit, 'recareas[0]');
        const recAreaName = get(this.props, 'recarea.[0].name');

        if (recAreaId && !recAreaName) {
          this.props.pActions.fetchRecarea(recAreaId);
        }

        const existingReservationId = get(this.props, 'location.query.reservationId', null);
        if (existingReservationId) {
          this.fetchExistingReservation(existingReservationId);
        }
      });

    // parse url parameters and initialize container
    const permitType = get(this.props, 'location.query.type', null);

    if (INYO_NF_PERMIT_TYPES.indexOf(permitType) > -1) {
      this.props.pActions.setPermitType(permitType);
    }
    else {
      this.props.pActions.setPermitType(OVERNIGHT_PERMIT);
    }

    const permitDate = get(this.props, 'location.query.date', null);

    if (moment(permitDate).isValid()) {
      this.props.pActions.setPermitDate(moment(permitDate).format(AVAILABILITY_KEY_MAP_FORMAT));
    }

    const activeTab = get(this.props, 'location.query.tab', null);

    if (activeTab === '0' || activeTab === '1') {
      this.props.pActions.setActiveTab(toNumber(activeTab));
    }

    if (SecurityHelper.isLoggedIn()) {
      if (!hasAccessToPermit(SecurityHelper.getAccount(), permitId)) {
        this.setState({ showCommercialAuthModal: true });
      }
    }
  }

  componentDidUpdate(prevProps) {
    this.getQueryParams(prevProps);
    
    if (!isEqual(prevProps.account, this.props.account)) {
      if (this.state.waitingForUserAccount && this.props.account) {
        this.setState({ waitingForUserAccount: false });
        this.bookCallBack()
      }
      else {
        if (SecurityHelper.isCommercialAccount()) {
          this.props.aActions.clearAvailabilitiesMap();
        }
        if (!hasAccessToPermit(SecurityHelper.getAccount(), this.props.match.params.permitId)) {
          this.setState({ showCommercialAuthModal: true });
          return;
        }
        const startDate = readRuleAsMoment("SeasonStart", this.props.permit.rules, this.props.permit.time_zone);
        if (startDate.isValid()) {
          this.props.aActions.fetchAvailabilityAllDivisions(this.props.match.params.permitId,
            startDate,
            undefined,
            SecurityHelper.isCommercialAccount(),
            this.state.existingIssuance ? this.state.existingIssuance.id : null
          );
        }
        if (!SecurityHelper.isCommercialAccount() && this.state.loginModalShown) {
          this.navigateToRegistration();
        }
      }
    }
  }

  componentWillUnmount() {
    this.props.pActions.clearFetchIssuanceError();
  }

  onBook(permitType) {
    this.bookCallBack = () => {
      this.book(permitType);
    };

    if (this.props.account) {
      this.bookCallBack();
    }
    else {
      this.props.pActions.showLoginModal();
      this.setState({ waitingForUserAccount: true });
    }
  }

  onFiltersApply(searchText, areas) {
    this.setState({ showFilters: false });
    this.props.pActions.setDivisionSearchCriteria({
      searchText,
      areas
    });
  }

  onFiltersClear() {
    this.props.pActions.setDivisionSearchCriteria({
      searchText: '',
      areas: []
    });
  }

  onFiltersClose() {
    this.setState({ showFilters: false });
  }

  onPermitDateChange(date) {
    this.props.pActions.setPermitDate(date.format(AVAILABILITY_KEY_MAP_FORMAT));
  }

  getQueryParams = (prevProps) => {
    const { location, permitType, permitDate } = this.props;
    if (prevProps.permitDate === permitDate && prevProps.permitType === permitType) {
      return;
    }
    const query = { ...location?.query };
    if (permitType) query.type = permitType;
    if (permitDate) query.date = permitDate;

    const queryString = Object.keys(query)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
      .join('&');

    window.history.replaceState({}, '', `?${queryString}`);
  }

  book(permitType) {
    if (!hasAccessToPermit(SecurityHelper.getAccount(), this.props.permit.id)) {
      this.setState({ showCommercialAuthModal: true });
      return;
    }
    this.props.pActions.updateIssuanceInProgress();
    const { permitId } = this.state;
    const groupInfo = [];
    for (let counter = 0; counter < this.state.selectedGroupSize; counter += 1) {
      groupInfo.push({
        first_name: '',
        middle_name: '',
        last_name: '',
        remark: ''
      });
    }
    const item = {
      permit_id: permitId,
      division_id: this.props.selectedDivision.id,
      entry_location_id: this.props.selectedDivision.id,
      user_id: SecurityHelper.getAccount().account_id,
      start_date: moment(this.props.selectedStartDate).utc(),
      status: issuanceStatuses.get(PERMIT_PRELIMINARY),
      group_info: groupInfo,
      group_size: groupInfo.length,
      is_visiting_mt_whitney: false
    };
    if (permitId === INYO_NF_PERMIT_ID) {
      item.permit_type = permitType;
    }

    if (isExitingMtWhitney(item)) {
      item.is_visiting_mt_whitney = true;
      item.end_date = moment(this.props.selectedStartDate2).utc();
      item.exit_location_id = INYO_MT_WHITNEY_EXIT_ID;
    }

    if (item.division_id === MT_WHITNEY_DAY_USE_DIVISION_ID) {
      item.end_date = item.start_date;
    }

    if (mustVisitMtWhitney(item)) {
      item.is_visiting_mt_whitney = true;
    }

    const { existingIssuance } = this.state;
    if (existingIssuance) {
      const oldStartDate = moment.utc(existingIssuance.start_date).clone();
      const newItem = Object.assign({}, existingIssuance, item, {
        id: existingIssuance.id,
        status: existingIssuance.status,
        is_modify_quota: true
      });

      if (moment.utc(newItem.end_date).isBefore(moment.utc(newItem.start_date))) {
        newItem.end_date = newItem.start_date;
      }

      if (!oldStartDate.isSame(moment.utc(newItem.start_date))) {
        this.props.pActions.clearOvernightCampsite();
        newItem.campsite_preferences = null;
      }

      this.props.pActions.updateIssuance(newItem).then(() => {
        const reservationId = `${this.state.existingIssuance.id}${RES_ID_DELIMITER}${permitId}`;
        const redirectURL = (`/permits/${permitId}/registration/${reservationId}`);
        this.props.history.push(redirectURL);
      });
    }
    else {
      this.props.pActions.addItem(PERMIT, item, false)
        .then(() => {
          if (!this.props.permitError) {
            const reservationId = `${this.props.issuance.id}${RES_ID_DELIMITER}${permitId}`;
            const redirectURL = (`/permits/${permitId}/registration/${reservationId}`);
            this.props.history.push(redirectURL);
          }
        })
        .catch(() => {
          this.setState({ showBookingErrorModal: true });
        });
    }
  }

  fetchExistingReservation = (existingReservationId) => {
    // load existing issuance
    const issuanceId = existingReservationId.split(RES_ID_DELIMITER)[0];
    const permitId = existingReservationId.split(RES_ID_DELIMITER)[1];
    this.props.pActions.fetchUnpaidIssuance(issuanceId, permitId)
      .then(() => {
        if (!this.props.issuanceError) {
          this.setState({ existingIssuance: this.props.issuance });
        }
      });
  }

  handleGroupSizeChange = (value) => {
    const maxGroupsize = INYO_GROUPSIZE_MAX_PERMIT_TYPE[this.props.permitType];
    if (toNumber(value) <= maxGroupsize) {
      this.setState({ selectedGroupSize: value });
      this.props.pActions.setSelectedDivision(null);
      this.props.pActions.setSelectedDivision2(null);
    }
  }

  isNotCommercialAccount = () => {
    return this.props.contactAgency === 'no' || SecurityHelper.isCommercialAccount();
  }

  handleDivisionDetailsClick = (divisionId) => {
    this.setState({
      selectedDivisionId: divisionId,
      isDivisionDetailsModalOpen: true,
    });
  }

  setIsDivisionDetailsModalOpen = () => this.setState({ isDivisionDetailsModalOpen: false });

  getInformationRequirements = () => {
    const hasSizeRequirement = this.state.selectedGroupSize <= 0;
    return {
        isRequired: hasSizeRequirement,
        hasSizeRequirement,
        divisionType: 'the sites',
    };
  };

  renderUnauthModal() {
    return (
      <UnauthorizedErrorModal
        isOpen={this.state.showCommercialAuthModal}
        closeModal={() => {
          this.setState({ showCommercialAuthModal: false });
          this.props.history.push(`/permits/${this.props.permit.id}`);
        }} />
    );
  }

  renderBookingErrorModal() {
    return (
      <BookingErrorModal
        errorMessage={get(this.props.permitError, 'error', 'An error has occurred. Please contact the system administrator.')}
        isOpen={this.state.showBookingErrorModal}
        closeModal={() => {
          this.setState({ showBookingErrorModal: false });
          // this.props.history.push(`/permits/${this.props.permit.id}`);
        }} />
    );
  }

  renderSide = () => {
    return (
      <DetailedAvailabilityPageSide>
        <div className="per-availability-map" id="map">
            <DivisionsMapContainer/>
        </div>
      </DetailedAvailabilityPageSide>
    );
  };

  renderAvailabilityFilter = (infoRequirements) => {
    const isMobileApp = SecurityHelper.isMobileApp();
    const notCommercial = this.isNotCommercialAccount();
    const maxGroupsize = INYO_GROUPSIZE_MAX_PERMIT_TYPE[this.props.permitType];
    const selectedGroupSize = this.state.selectedGroupSize;

    let filtersCount = 0;
    if (this.props.divisionSearchCriteria.searchText && this.props.divisionSearchCriteria.searchText !== '') {
      filtersCount += 1;
    }
    if (this.props.divisionSearchCriteria.areas && this.props.divisionSearchCriteria.areas.length > 0) {
      filtersCount += 1;
    }
    const AvailabilityFilterContainer = isMobileApp ? Stack : Inline;
    const groupSizeLabel = `Group Size ${maxGroupsize >= 0 ? `(${maxGroupsize} max)` : ''}`;

    return (
      <Stack>
        {isMobileApp && (
          <CommercialQuestion
            contactAgency={this.props.contactAgency}
            setQuestionnaireAnswers={this.props.pActions.setQuestionnaireAnswers}
          />
        )}
        {((notCommercial && isMobileApp) || !isMobileApp) && (
        <AvailabilityFilterContainer space={['md', 'sm']}>
          <DetailedAvailabilityFilterItem id="permit-date" mobileLabel="Select an entry date">
            <SingleDatePicker
              id="SingleDatePicker1"
              date={moment(this.props.permitDate)}
              numberOfMonths={1}
              focused={this.state.dateFocused}
              showDefaultInputIcon
              onDateChange={this.onPermitDateChange}
              onFocusChange={({ focused }) => this.setState({ dateFocused: focused })} />
          </DetailedAvailabilityFilterItem>
          <DetailedAvailabilityFilterItem id="group-size" mobileLabel={groupSizeLabel}>
              <NumberField
                  id="number-input"
                  min={0}
                  max={maxGroupsize}
                  value={selectedGroupSize}
                  onChange={this.handleGroupSizeChange}
                  units="group members" />
          </DetailedAvailabilityFilterItem>
          {!isMobileApp && (
            <DetailedAvailabilityFilterItem id="filer-button">
              <Button
                appearance="tertiary"
                iconBeforeElement={<Icons.IconFilterList />}
                onClick={() => {
                  this.setState({
                    showFilters: !this.state.showFilters
                  });
                }}>
                {filtersCount === 0 && <span>Filters</span>}
                {filtersCount > 0 && <span><strong>{filtersCount} Filter(s)</strong></span>}
              </Button>
            </DetailedAvailabilityFilterItem>
          )}
        </AvailabilityFilterContainer>
        )}

        {(notCommercial || !isMobileApp) && (this.state.showFilters || isMobileApp) && (
          <DetailedAvailabilityFilterItem id="filer-panel">
              <DivisionFilters
                areas={this.props.areas}
                searchCriteria={this.props.divisionSearchCriteria}
                onApply={this.onFiltersApply}
                onClear={this.onFiltersClear}
                onClose={this.onFiltersClose} />
          </DetailedAvailabilityFilterItem>
        )}

        {isMobileApp && infoRequirements.isRequired && notCommercial && (
          <DetailedAvailabilityInformationRequired {...infoRequirements} />
        )}
      </Stack>
    );
  }

  renderCalendarSection = (infoRequirements) => {
    const existingIssuanceId = this.state.existingIssuance ? this.state.existingIssuance.id : null;
    return (
      <DetailedAvailabilityGridAndActions>
        <LayoutControlContext.Consumer>
        {contextValue => {
          const { isMobileApp, isFullView } = contextValue;
          const isSmallerGrid = shouldUseSmallerGrid(contextValue);
          const daysToSkip = isSmallerGrid ?
            AVAILABILITY_DAYS_TO_SKIP_MOBILE :
            AVAILABILITY_DAYS_TO_SKIP;
          const numOfCols = isSmallerGrid ?
            AVAILABILITY_DAYS_TO_DISPLAY_MOBILE :
            AVAILABILITY_DAYS_TO_DISPLAY;

          if(infoRequirements.isRequired) return null;

          return (
            <>
              {isMobileApp &&
                <Box className="mb-2 pt-1" background="extra-light-blue">
                  <AvailabilityLegend />
                </Box>
              }
              <AvailabilityEnterInyoContainer
                selectedGroupSize={this.state.selectedGroupSize}
                onBook={this.onBook}
                existingIssuanceId={existingIssuanceId}
                waitingForUserAccount={this.state.waitingForUserAccount}
                isSmallerGrid={isSmallerGrid}
                daysToSkip={daysToSkip}
                numOfCols={numOfCols}
                isFullView={isFullView}
                onDivisionDetailsClick={this.handleDivisionDetailsClick}
              />
            </>
          );
        }}
        </LayoutControlContext.Consumer>
      </DetailedAvailabilityGridAndActions>
    );
  }

  renderMain = () => {
    const isMobileApp = SecurityHelper.isMobileApp();
    const infoRequirements = this.getInformationRequirements();
    const notCommercial = this.isNotCommercialAccount();
    const recAreaName = get(this.props, "recarea[0].name", "");
    const recAreaId = get(this.props, "recarea[0].entity_id", "");
    const { permit } = this.props;
    const { name } = permit;

    const path = [
      { title: 'Home', url: '/' },
      { title: recAreaName, url: `/camping/gateways/${recAreaId}` },
      { title: name, url: `/permits/${permit.id}` },
      { title: 'Detailed Availability' }
    ];
    
    return (
      <DetailedAvailabilityPageMain breadcrumbPath={path}>
        <DetailedAvailabilityFilter>
          {this.renderAvailabilityFilter(infoRequirements)}
        </DetailedAvailabilityFilter>

        {!isMobileApp && (
          <CommercialQuestion
            isAllowOnDetailedAvailability
            contactAgency={this.props.contactAgency}
            setQuestionnaireAnswers={this.props.pActions.setQuestionnaireAnswers}
          />
        )}
        {infoRequirements.isRequired && notCommercial && (
          <DetailedAvailabilityInformationRequired {...infoRequirements} />
        )}
        {this.renderCalendarSection(infoRequirements)}
      </DetailedAvailabilityPageMain>
    );
  }

  render() {
    return (
      <DetailedAvailabilityPageLayout isInformationRequired={this.getInformationRequirements().isRequired}>
        <DivisionDetailsModal
          permit={this.props.permit}
          divisionId={this.state.selectedDivisionId}
          hasMap
          isOpen={this.state.isDivisionDetailsModalOpen}
          onRequestClose={this.setIsDivisionDetailsModalOpen} />
        {this.renderMain()}
        {this.renderSide()}
        {this.renderUnauthModal()}
        {this.renderBookingErrorModal()}
      </DetailedAvailabilityPageLayout>)
  }
}

AvailabilityPage.propTypes = propTypes;
AvailabilityPage.defaultProps = defaultProps;

const select = (state) => {
  return {
    account: state.auth.account,
    areas: state.inyonwhitney.areas.list,
    divisionSearchCriteria: state.inyonwhitney.divisionSearchCriteria,
    issuance: state.inyonwhitney.permit.issuance,
    permit: state.inyonwhitney.permit.result,
    permitError: state.inyonwhitney.permit.error,
    issuanceError: state.inyonwhitney.permit.fetch_issuance_error,
    permitType: state.inyonwhitney.permitType,
    permitDate: state.inyonwhitney.permitDate,
    recarea: state.inyonwhitney.permit.recarea,
    selectedDivision: state.inyonwhitney.selectedDivision,
    selectedStartDate: state.inyonwhitney.selectedStartDate,
    selectedStartDate2: state.inyonwhitney.selectedStartDate2,
    contactAgency: state.inyonwhitney.questionnaire.contactAgency,
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    pActions: bindActionCreators(permitActions, dispatch),
    aActions: bindActionCreators(availabilityActions, dispatch),
  };
}


export default connect(select, mapDispatchToProps)(AvailabilityPage);
